import { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X, ChevronRight, TrendingDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const POLL_INTERVAL_MS = 5 * 60 * 1000;        // refresh every 5 min
const SESSION_DISMISS_KEY = 'mech_perf_alerts_dismissed_v1';

function readDismissed() {
  try { return JSON.parse(sessionStorage.getItem(SESSION_DISMISS_KEY) || '[]'); }
  catch { return []; }
}
function writeDismissed(list) {
  try { sessionStorage.setItem(SESSION_DISMISS_KEY, JSON.stringify(list)); } catch (_) { /* noop */ }
}

/**
 * Global banner showing under-performing mechanics. Renders on every page
 * (mounted in Layout.jsx) for super_admin / service_manager / floor_manager.
 *
 * Severity scaling:
 *  - one alert → compact bar
 *  - 2-3 alerts → expanded list
 *  - 4+ → expanded list with "see all" if collapsed
 *
 * Dismiss persists for the session per-mechanic.
 */
export default function MechanicPerformanceAlertBanner({ user }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [dismissedIds, setDismissedIds] = useState(readDismissed);
  const intervalRef = useRef(null);

  const allowedRole = user && ['super_admin', 'service_manager', 'floor_manager'].includes(user.role);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/super-admin/alerts/mechanic-performance`,
        { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (!allowedRole) return;
    fetchAlerts();
    intervalRef.current = setInterval(fetchAlerts, POLL_INTERVAL_MS);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [allowedRole, fetchAlerts]);

  if (!allowedRole || !data) return null;
  const visibleAlerts = (data.alerts || []).filter(a => !dismissedIds.includes(a.mechanic_id));
  if (visibleAlerts.length === 0) return null;

  const dismissOne = (mid) => {
    const next = [...new Set([...dismissedIds, mid])];
    setDismissedIds(next);
    writeDismissed(next);
  };
  const dismissAll = () => {
    const next = [...new Set([...dismissedIds, ...visibleAlerts.map(a => a.mechanic_id)])];
    setDismissedIds(next);
    writeDismissed(next);
  };

  const trackedRange = data.tracked_week_ranges
    ?.map(w => `W${w.week} (${w.start_day}–${w.end_day})`)
    .join(' & ') || 'recent weeks';

  return (
    <Card
      className="mb-4 border-l-4 border-rose-500 bg-rose-50/80 backdrop-blur-sm shadow-sm overflow-hidden"
      data-testid="mech-perf-alert-banner"
    >
      {/* Header bar (always visible) */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-rose-100/50 transition-colors text-left"
        data-testid="mech-perf-alert-toggle"
      >
        <AlertTriangle className="h-5 w-5 text-rose-600 flex-shrink-0 animate-pulse" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-rose-900">
            {visibleAlerts.length} mechanic{visibleAlerts.length === 1 ? '' : 's'} under-performing
            <span className="font-normal text-rose-700"> — below {data.threshold_pct}% for {data.consecutive_weeks} weeks straight</span>
          </p>
          {!expanded && (
            <p className="text-xs text-rose-700 truncate mt-0.5">
              {visibleAlerts.slice(0, 3).map(a => `${a.mechanic_name} (${a.avg_pct.toFixed(0)}%)`).join(' · ')}
              {visibleAlerts.length > 3 && ` +${visibleAlerts.length - 3} more`}
            </p>
          )}
        </div>
        <ChevronRight className={`h-4 w-4 text-rose-700 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); dismissAll(); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); dismissAll(); } }}
          className="ml-1 p-1 rounded hover:bg-rose-200/70 cursor-pointer"
          data-testid="mech-perf-alert-dismiss-all"
          title="Dismiss all for this session"
        >
          <X className="h-4 w-4 text-rose-700" />
        </span>
      </button>

      {/* Expanded list */}
      {expanded && (
        <div className="px-4 pb-3 border-t border-rose-200" data-testid="mech-perf-alert-list">
          <div className="flex items-center justify-between text-xs text-rose-700 py-2 gap-2 flex-wrap">
            <span>
              Tracking: {trackedRange}
              {data.used_fallback && (
                <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-rose-200/60 text-rose-800 text-[10px] font-medium">
                  showing previous month — current has no completed weeks yet
                </span>
              )}
            </span>
            <button
              className="hover:underline font-medium"
              onClick={() => navigate('/financials')}
              data-testid="mech-perf-alert-view-report"
            >
              Open full report →
            </button>
          </div>
          <ul className="space-y-1.5">
            {visibleAlerts.map(a => (
              <li
                key={a.mechanic_id}
                className="flex items-center gap-3 px-3 py-2 bg-white/70 border border-rose-200 rounded-md"
                data-testid={`mech-perf-alert-${a.mechanic_id}`}
              >
                <TrendingDown className="h-4 w-4 text-rose-500 flex-shrink-0" />
                <span className="font-medium text-slate-900 text-sm flex-1 truncate">{a.mechanic_name}</span>
                <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-slate-600">
                  {a.weekly_pcts.map((p, i) => (
                    <span
                      key={i}
                      className={`px-1.5 py-0.5 rounded font-mono text-[10px] ${
                        p < 25 ? 'bg-rose-200 text-rose-900' : 'bg-amber-100 text-amber-900'
                      }`}
                      title={`Week ${a.weeks_below_threshold[i]}: ${a.weekly_hours[i]}h`}
                    >
                      {p.toFixed(0)}%
                    </span>
                  ))}
                </span>
                <span className="text-sm font-bold text-rose-700 w-12 text-right">
                  avg {a.avg_pct.toFixed(0)}%
                </span>
                <button
                  onClick={() => dismissOne(a.mechanic_id)}
                  className="text-slate-400 hover:text-rose-600 p-1"
                  title="Dismiss for this session"
                  data-testid={`mech-perf-alert-dismiss-${a.mechanic_id}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
